import React from 'react';
import Form from 'react-bootstrap/Form';
import { Field, getFormValues, reduxForm, SubmissionError } from 'redux-form';
import { renderCheckbox, renderField, renderReactSelect } from '../../../lib/helper/form';

import { connect } from 'react-redux';
import { addToast } from '../../../lib/redux/actions/toast';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import { PAYOR_TYPES, PAYOR_TYPE_TERMS } from '../../../lib/helper/invoice';
import { isAdmin } from '../../../lib/helper/authorisation';

const mapStateToProps = (state, props) => {
  return {
    userData: state.auth.userData,
    currentFormValues: getFormValues('ReviewInvoiceDataForm')(state),
  };
};
const mapDispatchToProps = {
  addToast,
};

class ReviewInvoiceDataForm extends React.Component {
  constructor(props) {
    super(props);

    const { initialize } = this.props;

    // Match hospital from invoice PDF against invoice batch hospitals
    const hospitalValue =
      props.invoiceData?.potentialHospital && props.invoiceBatch
        ? props.hospitals
            .filter((h) => {
              const potentialLower = props.invoiceData.potentialHospital.toLocaleLowerCase();
              const batchHospitals = props.invoiceBatch.hospitals || [];

              // Check if this hospital is directly on the batch
              const isOnBatch = batchHospitals.some((bh) => bh.id === h.id);

              // Check if this hospital's billToHospital name matches the extracted name
              const hasBillToMatch = h.billToHospital && h.billToHospital.name.toLocaleLowerCase() === potentialLower;

              // Match if:
              // 1. Hospital is on batch AND its billToHospital name matches extracted name
              return isOnBatch && hasBillToMatch;
            })
            .map((h) => h.billToHospital) // Use the billToHospital, not the hospital itself
        : null;

    const payorTypeCode = props.invoiceData?.payorTypeCode ?? PAYOR_TYPES.PATIENT;
    const payorTypeLabel = PAYOR_TYPE_TERMS[payorTypeCode] || 'Self Pay';

    const initVals = {
      ...props.invoiceData,
      patientDOB: props.invoiceData?.patientDOB ? moment(props.invoiceData.patientDOB, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
      invoiceDate: props.invoiceData?.invoiceDate ? moment(props.invoiceData.invoiceDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
      shortfallInvoiceDate: props.invoiceData?.shortfallInvoiceDate ? moment(props.invoiceData.shortfallInvoiceDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
      serviceDate: props.invoiceData?.serviceDate ? moment(props.invoiceData.serviceDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
      invoiceAmount: props.invoiceData?.invoiceAmount.replace(/[£,]/g, ''),
      amountDue: props.invoiceData?.amountDue.replace(/[£,]/g, ''),
      hospital: hospitalValue && hospitalValue.length > 0 ? hospitalValue[0] : null,
      payorTypeCode: payorTypeCode,
    };

    this.state = {
      selectedHospital: hospitalValue && hospitalValue.length > 0 ? { value: hospitalValue[0].id, label: hospitalValue[0].name } : null,
      selectedPayorType: { value: payorTypeCode, label: payorTypeLabel },
    };

    initialize(initVals);
  }

  onSubmit(values) {
    const invoiceData = { ...this.props.invoiceData, ...values };
    if (invoiceData?.invoiceBatchConsultantId && invoiceData?.consultant?.id && invoiceData.invoiceBatchConsultantId !== invoiceData.consultant.id) {
      // consultantMismatch between invoiceBatch and invoice.
      return;
    }
    // Use the selected payor type value from the dropdown
    const payorTypeCode = this.state.selectedPayorType?.value ?? invoiceData.payorTypeCode ?? PAYOR_TYPES.PATIENT;
    invoiceData.payorTypeCode = payorTypeCode;

    if (payorTypeCode === PAYOR_TYPES.HOSPITAL && this.state.selectedHospital === null) {
      throw new SubmissionError({ hospital: 'No hospital selected' });
    }
    if (payorTypeCode === PAYOR_TYPES.HOSPITAL && !invoiceData.hospital?.id && this.state.selectedHospital?.value) {
      invoiceData.hospital = { id: this.state.selectedHospital.value, name: this.state.selectedHospital.label };
    }
    // create a new object where values overwrites this.props.invoiceData
    delete invoiceData.specialist;
    delete invoiceData.userChecked;
    delete invoiceData.invoiceBatchConsultantId;
    this.props.onSubmit(invoiceData);
  }

  render() {
    const { handleSubmit, error, pristine, submitting, currentFormValues, invoiceData, hospitals = [], invoiceBatch = undefined } = this.props;

    const payorTypeOptions = Object.entries(PAYOR_TYPE_TERMS).map(([value, label]) => ({
      value: parseInt(value),
      label,
    }));

    // Build list of hospitals for dropdown - if a hospital has billToHospital, use that instead
    const hospitalMap = new Map();
    hospitals
      .filter((h) => invoiceData?.consultant?.id && h.consultantIds.indexOf(invoiceData?.consultant?.id) > -1)
      .forEach((h) => {
        const targetHospital = h.billToHospital || h;
        hospitalMap.set(targetHospital.id, targetHospital);
      });

    const availableHospitals = Array.from(hospitalMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((h) => ({ value: h.id, label: h.name }));

    const consultantMismatch =
      invoiceData?.invoiceBatchConsultantId && invoiceData?.consultant?.id && invoiceData.invoiceBatchConsultantId !== invoiceData.consultant.id;

    // does the selected hospital match one of the hospitals on the invoice batch if the invoice batch exists.
    // OR does it match one of the billToHospitals from the hospitals connected to the invoicebatch
    const hospitalMatch =
      this.state?.selectedHospital?.value &&
      invoiceBatch &&
      invoiceBatch.hospitals?.some(
        (h) => h.id === Number(this.state?.selectedHospital?.value) || h.billToHospital?.id === Number(this.state?.selectedHospital?.value),
      );

    const activePayorTypeCode = this.state.selectedPayorType?.value ?? currentFormValues?.payorTypeCode ?? invoiceData.payorTypeCode ?? PAYOR_TYPES.PATIENT;
    const suspectWrongPayorTypeCodeForType = !!(activePayorTypeCode === PAYOR_TYPES.PATIENT && this.state.selectedHospital !== null);

    const canProceed = true; // !invoiceData.existingInvoiceId && !consultantMismatch && !!invoiceData?.consultant?.id; // && !suspectWrongPayorTypeCodeForType;

    return (
      <div className="row">
        <div className="col">
          <Row className={'mb-3 align-items-center'}>
            <Col>
              <h4 className={'mb-0'}>Confirm Details</h4>
            </Col>
          </Row>
          {error && <div className="alert alert-danger">{error}</div>}
          <Form onSubmit={handleSubmit(this.onSubmit.bind(this))}>
            <Row className={'mb-3'}>
              <Col md={8}>
                <p>
                  Consultant: {invoiceData?.consultant?.name ?? <strong>NOT FOUND</strong>}{' '}
                  {invoiceData?.consultant?.name && !invoiceData?.consultant?.useAutomatedChasing && (
                    <>
                      <br />
                      <strong style={{ color: 'red' }}>AUTO CHASING DISABLED</strong>
                    </>
                  )}
                </p>
              </Col>
              <Col md={4}>
                <Field
                  name="payorTypeCode"
                  selectedOptions={this.state.selectedPayorType}
                  handleSelect={(selectedPayorType) => this.setState({ selectedPayorType })}
                  label="Payor Type"
                  options={payorTypeOptions}
                  component={renderReactSelect}
                />
              </Col>
            </Row>

            {consultantMismatch && <div className="alert alert-danger text-center">The invoice consultant does not match this invoice batch</div>}
            <Row>
              <Col className={'col-lg-8'}>
                <Field required name="invoiceNo" label="Invoice Number" type="text" component={renderField} />
              </Col>
              <Col className={'col-lg-4'}>
                <Field
                  required
                  name="invoiceDate"
                  label={currentFormValues?.isShortfall ? 'Shortfall Date' : 'Invoice Date'}
                  type="date"
                  component={renderField}
                />
              </Col>
            </Row>
            {currentFormValues?.isShortfall ? (
              <Row>
                <Col className={'col-lg-4'}>
                  <Field required name="shortfallInvoiceDate" label={'Original Invoice Date'} type="date" component={renderField} />
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col className={'col-lg-8'}>
                <Field required name="patientName" label="Patient Name" type="text" component={renderField} />
              </Col>
              <Col className={'col-lg-4'}>
                <Field required name="patientDOB" label="Patient DOB" type="date" component={renderField} />
              </Col>
            </Row>

            <Row>
              <Col className={'col-lg-4'}>
                <Field name="serviceDate" label="Service Date" type="date" component={renderField} />
              </Col>
              <Col className={'col-lg-4'}>
                <Field required name="invoiceAmount" label="Invoice Amount (£)" type="number" step=".01" component={renderField} />
              </Col>
              <Col className={'col-lg-4'}>
                <Field
                  required
                  name="amountDue"
                  label={currentFormValues?.isShortfall ? 'Shortfall Amount (£)' : 'O/S Amount (£)'}
                  type="number"
                  step=".01"
                  component={renderField}
                />
              </Col>
            </Row>
            <Row>
              {activePayorTypeCode === PAYOR_TYPES.HOSPITAL && (
                <Col>
                  <Field
                    required
                    name="hospital"
                    selectedOptions={this.state.selectedHospital}
                    handleSelect={(selectedHospital) => {
                      this.setState({ selectedHospital });
                    }}
                    label="Hospital"
                    options={availableHospitals}
                    component={renderReactSelect}
                  />
                </Col>
              )}
            </Row>
            {activePayorTypeCode === PAYOR_TYPES.PATIENT ? (
              <Row>
                <Col>
                  <Field name="subscriberNumber" label="Subscriber Number (Insurer Reg.)" type={'text'} component={renderField} />
                </Col>
                <Col>
                  <Field name="claimNumber" label="Claim Number" type={'text'} component={renderField} />
                </Col>
              </Row>
            ) : null}

            <Row>
              {activePayorTypeCode === PAYOR_TYPES.PATIENT ? (
                <Col className={'col-lg-4'}>
                  <Field name="isShortfall" label="Shortfall?" id="is-shortfall" component={renderCheckbox} />{' '}
                </Col>
              ) : null}
              <Col>
                <Field
                  required
                  name="userChecked"
                  label="I confirm I've checked these details"
                  id="user-checked"
                  component={renderCheckbox}
                  className={'text-right'}
                />
              </Col>
            </Row>

            <hr />
            {suspectWrongPayorTypeCodeForType ? (
              <Alert variant={'warning'} className={'text-center'}>
                You are uploading a self pay invoice but this invoice has a hospital. Is this correct?
              </Alert>
            ) : null}
            {canProceed ? (
              <>
                {invoiceBatch && hospitalMatch === false && (
                  <Row className={'mb-3'}>
                    <Col>
                      <Alert variant={'danger'} className={'text-center'}>
                        The selected hospital does not match the hospital on this invoice batch.
                      </Alert>
                    </Col>
                  </Row>
                )}
                <button
                  type="submit"
                  disabled={pristine || submitting || !currentFormValues.userChecked || (invoiceBatch && hospitalMatch === false && !isAdmin())}
                  className="btn btn-primary float-right">
                  {activePayorTypeCode === PAYOR_TYPES.HOSPITAL ? `Confirm Details` : `Lookup Contact Details`}
                </button>
              </>
            ) : (
              <Alert variant={'danger'} className={'text-center'}>
                You cannot upload this invoice{!!invoiceData?.invoiceBatchId ? ' against this batch' : ''}.
              </Alert>
            )}
          </Form>
        </div>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'ReviewInvoiceDataForm',
  })(ReviewInvoiceDataForm),
);
